import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosRequestConfig } from 'axios';
import type { OrderForm } from 'types/OrderForm';
import { ENDPOINT } from '@constants/endpoint';
import { filterOfferWithoutCalorifics } from '@services/OrderForm.serviceTS';
import axios from '@utils/axiosInstance';

type Selector<T> = (data: OrderForm | { [key: string]: never }) => T;
const STALE_TIME_MS = 1000 * 60 * 10;

export const getOrderForm = async (config: AxiosRequestConfig) => {
  const { data } = await axios.get<OrderForm>(ENDPOINT.ORDER_FORM, config);

  return filterOfferWithoutCalorifics(data);
};

const useOrderForm = (options = {}) => {
  const query = useQuery({
    queryKey: ['orderForm'],
    queryFn: getOrderForm,
    staleTime: STALE_TIME_MS,
    ...options,
  });

  return {
    ...query,
    // data is always available because query is prefetched in pages/_app.jsx
    data: query.data as NonNullable<typeof query.data>,
  };
};

export const useOrderFormQuery = () => {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME
  return queryClient.getQueryData<OrderForm>(['orderForm'])!;
};

export const useOrderFormSelector = <T>(selector: Selector<T>): T => {
  const { data } = useOrderForm({ enabled: false });

  return selector(data || {});
};

export const selectOrderForm = (data: OrderForm) => data;
export const selectDiets = (data: OrderForm) => data.diets;

export default useOrderForm;
